import { Button, Form, Input, message, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import './CreateModalDevice.scss';
import { useTranslation } from '../../../i18n/i18n';
import {
  DeviceDriverType,
  DeviceSensorSewerLocation,
  DeviceSensorType,
  DeviceSensorUltrasonicModel,
  DeviceType,
} from '../../../gen/proto/api/gateway/v1/device_pb';
import ModalCustom from '../ModalCustom/ModalCustom';
import useAuthStore from '../../../stores/auth.store';
import useTenantStore, { ALL_TENANTS_VALUE } from '../../../stores/tenant.store';
import { ConnectError } from '@connectrpc/connect';

export interface DeviceFormValues {
  name: string;
  type: DeviceType;
  sensorType?: DeviceSensorType;
  sewerLocation?: DeviceSensorSewerLocation;
  ultrasonicModel?: DeviceSensorUltrasonicModel;
  driverModel?: DeviceDriverType;
  tenantUuid?: string;
}

interface CreateModalProps {
  open: boolean;
  onCancel: () => void;
  handleSubmit: (values: DeviceFormValues) => Promise<void>;
  handleEdit: (values: DeviceFormValues) => Promise<void>;
  mode?: 'create' | 'edit';
  initialValues?: DeviceFormValues;
}

const CreateModalDevice = (props: CreateModalProps) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('devices', { keyPrefix: 'modal' });
  const isEdit = props.mode === 'edit';
  const [type, setType] = useState<DeviceType | undefined>(props.initialValues?.type);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const authTenantUuid = useAuthStore((state) => state.tenantUuid);
  const tenants = useTenantStore((state) => state.tenants);
  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        form.setFieldsValue(props.initialValues);
        setType(props.initialValues.type);
      } else {
        form.resetFields();
        if (authTenantUuid) {
          form.setFieldValue('tenantUuid', authTenantUuid);
        }
        setType(undefined);
      }
    }
  }, [props.open, props.initialValues, form, authTenantUuid]);

  const handleOk = async () => {
    try {
      setIsLoading(true);
      const values = await form.validateFields();
      isEdit ? await props.handleEdit(values) : await props.handleSubmit(values);
      form.resetFields();
    } catch (error) {
      if (error instanceof ConnectError && error.rawMessage.includes('device_name_unique')) {
        messageApi.error(t('deviceNameTaken'));
      } else {
        messageApi.error(t('common:errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      {contextHolder}
      <ModalCustom
        title={isEdit ? t('edit_title') : t('title')}
        description={isEdit ? t('editDesc') : t('createDesc')}
        isModalOpen={props.open}
        setIsModalOpen={props.onCancel}
        footer={
          <Button key="submit" type="primary" onClick={handleOk} loading={isLoading}>
            {isEdit ? t('save') : t('create')}
          </Button>
        }
      >
        <div className={'CreateModal__container'}>
          <Form
            name="deviceGroup"
            form={form}
            layout="vertical"
            onFinish={isEdit ? handleOk : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.defaultPrevented) {
                e.preventDefault();
                form.submit();
              }
            }}
          >
            <Form.Item
              name="name"
              label={t('name_label')}
              rules={[
                { required: true, message: t('name_required') },
                { min: 3, message: t('name_min_length') },
              ]}
            >
              <Input type="text" placeholder={t('name_placeholder')} />
            </Form.Item>
            <Form.Item name="type" label={t('type_label')} rules={[{ required: true, message: t('type_required') }]}>
              <Select
                placeholder={t('type_placeholder')}
                onChange={(value: DeviceType) => {
                  setType(value);
                  form.resetFields(['sensorType', 'sewerLocation', 'ultrasonicModel', 'driverModel']);
                }}
                options={[
                  { value: DeviceType.SENSOR, label: t('type_sensor') },
                  { value: DeviceType.DRIVER, label: t('type_driver') },
                ]}
              />
            </Form.Item>

            {type === DeviceType.SENSOR && (
              <>
                <Form.Item
                  name="sensorType"
                  label={t('sensor_type_label')}
                  rules={[{ required: true, message: t('sensor_type_required') }]}
                >
                  <Select
                    placeholder={t('sensor_type_placeholder')}
                    options={[{ value: DeviceSensorType.ULTRASONIC, label: t('sensor_type_ultrasonic') }]}
                  />
                </Form.Item>
                <Form.Item
                  name="sewerLocation"
                  label={t('sewer_location_label')}
                  rules={[{ required: true, message: t('sewer_location_required') }]}
                >
                  <Select
                    placeholder={t('sewer_location_placeholder')}
                    options={[
                      { value: DeviceSensorSewerLocation.PUBLIC, label: t('sewer_location_public') },
                      { value: DeviceSensorSewerLocation.PRIVATE, label: t('sewer_location_private') },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="ultrasonicModel"
                  label={t('ultrasonic_model_label')}
                  rules={[{ required: true, message: t('ultrasonic_model_required') }]}
                >
                  <Select
                    placeholder={t('ultrasonic_model_placeholder')}
                    options={[{ value: DeviceSensorUltrasonicModel.US100, label: t('ultrasonic_model_us100') }]}
                  />
                </Form.Item>
              </>
            )}

            {type === DeviceType.DRIVER && (
              <Form.Item
                name="driverModel"
                label={t('driver_model_label')}
                rules={[{ required: true, message: t('driver_model_required') }]}
              >
                <Select
                  placeholder={t('driver_model_placeholder')}
                  options={[{ value: DeviceDriverType.DRIVER_PRO_V1, label: t('driver_model_pro_v1') }]}
                />
              </Form.Item>
            )}
            {isAll && (
              <Form.Item name="tenantUuid" label={t('tenant_label')}>
                <Select options={tenants} disabled={!isSuperAdmin} />
              </Form.Item>
            )}
          </Form>
        </div>
      </ModalCustom>
    </>
  );
};

export default CreateModalDevice;
